import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getGatewayPaymentStatus } from '../../api/api';
import './MembershipPaymentCallback.css';

// How long to keep auto-polling a still-pending payment before handing off
// to the Members page's Pending modal — HDFC's Order Status API can lag a
// few seconds behind the actual charge completing, which is why a single
// one-shot check right after the redirect isn't enough.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30000;

// How long the verified success screen stays up (HDFC security audit
// requirement: a visible confirmation with the order/amount/reference)
// before handing off to the Members page.
const SUCCESS_REDIRECT_DELAY_MS = 3000;

const formatAmount = (amount) => {
  const value = Number(amount || 0);
  return `₹${value.toLocaleString('en-IN')}`;
};

// Landing page HDFC redirects the browser back to after a membership
// deposit checkout. Mirrors Parents/Payments/ParentPaymentCallback.jsx: the
// browser redirect itself is never trusted — the backend re-confirms the
// real status (re-queries HDFC / relies on the webhook) before anything is
// shown as successful, and a page refresh re-asks the backend the same way
// (which itself never reports a previously-applied 'success' back down to
// 'pending' — see verifyAndSyncPaymentStatus's short-circuit). This page
// never applies/creates a payment itself — it only ever reads status and,
// once resolved, hands a presentation-only summary to the Members page.
const MembershipPaymentCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');

  const [status, setStatus] = useState('checking'); // checking | success | pending | failed | cancelled | verification_error
  const [message, setMessage] = useState('Confirming payment...');
  const [details, setDetails] = useState(null);
  const [polling, setPolling] = useState(false);

  const activeRef = useRef(true);
  const intervalIdRef = useRef(null);
  const redirectTimeoutIdRef = useRef(null);
  const pollStartRef = useRef(null);
  const lastResultRef = useRef(null);

  const stopPolling = () => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    setPolling(false);
  };

  const stopRedirectTimer = () => {
    if (redirectTimeoutIdRef.current) {
      clearTimeout(redirectTimeoutIdRef.current);
      redirectTimeoutIdRef.current = null;
    }
  };

  // Single status check — used for the initial load and each 3s poll tick.
  // Always defers entirely to the backend's re-verified status (see
  // getGatewayPaymentStatus / verifyAndSyncPaymentStatus); never reads/
  // trusts anything from the URL itself beyond which order to ask about.
  // Returns the raw backend result (plus a normalized `status`) so the
  // caller can build the Members-page hand-off state without relying on
  // stale component state.
  const checkStatus = useCallback(async () => {
    if (!orderId) {
      setStatus('verification_error');
      setMessage(
        'Payment verification could not be completed. If an amount was deducted, please check the Members list before retrying.'
      );
      return { status: 'verification_error' };
    }

    try {
      const result = await getGatewayPaymentStatus(orderId);
      if (!activeRef.current) return null;

      const finalStatus = String(result?.status || 'pending').toLowerCase();
      const normalized = { ...result, status: finalStatus };
      lastResultRef.current = normalized;

      setStatus(finalStatus);
      setDetails({
        orderNumber: result?.order_id || orderId,
        amount: result?.amount,
        gatewayReference: result?.gateway_reference || null
      });

      if (finalStatus === 'success') {
        setMessage('Payment confirmed. The membership has been activated.');
      } else if (finalStatus === 'cancelled') {
        setMessage('The payment was cancelled. The membership remains pending — you can retry.');
      } else if (finalStatus === 'failed') {
        setMessage('The payment did not go through. The membership remains pending — you can retry.');
      } else {
        setMessage('Payment is still being confirmed. Please check again shortly.');
      }

      return normalized;
    } catch (error) {
      // A 401 that survives the shared axios interceptor's refresh attempt
      // means the session is unrecoverable — authSession.forceLogout()
      // (triggered by the interceptor itself) already clears state and
      // hard-navigates to /login; this component doesn't need its own
      // logout handling, only to avoid showing a misleading "payment
      // failed" flash while that redirect is in flight.
      if (!activeRef.current) return null;
      setStatus('failed');
      setMessage('Could not confirm the payment. Please check the Members list or try again.');
      return { status: 'failed' };
    }
  }, [orderId]);

  useEffect(() => {
    activeRef.current = true;
    stopPolling();
    stopRedirectTimer();

    // Hands a presentation-only summary to the Members page — the backend
    // has already verified/applied the payment by this point (or hasn't,
    // for pending/failed); nothing here re-decides or re-applies anything.
    const goToMembers = (finalStatus, result) => {
      navigate('/librarian/members', {
        state: {
          paymentResult: {
            status: finalStatus,
            orderNumber: result?.order_id || orderId,
            amount: result?.amount,
            gatewayReference: result?.gateway_reference || null,
            membershipId: result?.membership_id || null
          }
        }
      });
    };

    const run = async () => {
      const first = await checkStatus();
      if (!activeRef.current || first === null) return;

      if (first.status === 'success') {
        redirectTimeoutIdRef.current = setTimeout(() => {
          if (activeRef.current) goToMembers('success', first);
        }, SUCCESS_REDIRECT_DELAY_MS);
        return;
      }

      if (first.status === 'failed' || first.status === 'cancelled') {
        goToMembers(first.status, first);
        return;
      }

      if (first.status === 'verification_error') {
        return;
      }

      // Still pending — start the 3s poll loop.
      pollStartRef.current = Date.now();
      setPolling(true);

      intervalIdRef.current = setInterval(async () => {
        if (!activeRef.current) return;

        if (Date.now() - pollStartRef.current >= POLL_TIMEOUT_MS) {
          stopPolling();
          goToMembers('pending', lastResultRef.current || { order_id: orderId });
          return;
        }

        const next = await checkStatus();
        if (!activeRef.current || next === null) return;

        if (next.status === 'success') {
          stopPolling();
          redirectTimeoutIdRef.current = setTimeout(() => {
            if (activeRef.current) goToMembers('success', next);
          }, SUCCESS_REDIRECT_DELAY_MS);
          return;
        }

        if (next.status === 'failed' || next.status === 'cancelled') {
          stopPolling();
          goToMembers(next.status, next);
        }
        // else: still pending, keep polling
      }, POLL_INTERVAL_MS);
    };

    run();

    return () => {
      activeRef.current = false;
      stopPolling();
      stopRedirectTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const icon =
    status === 'success' ? 'bx-check-circle text-success'
      : status === 'cancelled' ? 'bx-block text-secondary'
        : status === 'failed' ? 'bx-x-circle text-danger'
          : status === 'verification_error' ? 'bx-error-circle text-warning'
            : 'bx-time-five text-warning';

  const title =
    status === 'success' ? 'Payment Successful'
      : status === 'cancelled' ? 'Payment Cancelled'
        : status === 'failed' ? 'Payment Failed'
          : status === 'verification_error' ? 'Payment Verification Error'
            : 'Payment Pending';

  return (
    <div className="pmt-callback-page">
      <div className="pmt-callback-card">
        <div className="pmt-callback-card__body">
          {status === 'checking' ? (
            <div className="py-4">Confirming payment...</div>
          ) : (
            <>
              <i className={`bx ${icon} pmt-callback-card__icon`} />
              <h4 className="pmt-callback-card__title">{title}</h4>
              <p className="pmt-callback-card__message">{message}</p>

              {status === 'pending' && polling && (
                <p className="pmt-callback-card__hint">
                  <span className="pmt-callback-card__spinner" aria-hidden="true" />
                  Checking again automatically…
                </p>
              )}

              {status === 'success' && details && (
                <div className="pmt-callback-card__audit">
                  <div className="pmt-callback-card__audit-row">
                    <span>Order Number</span>
                    <strong>{details.orderNumber}</strong>
                  </div>
                  <div className="pmt-callback-card__audit-row">
                    <span>Amount</span>
                    <strong>{formatAmount(details.amount)}</strong>
                  </div>
                  {details.gatewayReference && (
                    <div className="pmt-callback-card__audit-row">
                      <span>Transaction / Gateway Reference</span>
                      <strong>{details.gatewayReference}</strong>
                    </div>
                  )}
                </div>
              )}

              {orderId && status !== 'success' && (
                <p className="pmt-callback-card__ref">Reference: {orderId}</p>
              )}

              <div className="pmt-callback-card__actions">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/librarian/members/register')}
                >
                  Register Another Member
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/librarian/members')}
                >
                  View Members
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipPaymentCallback;
